import { Company } from 'models'
import { isOrphanParentOption } from './constants'
import type { CompaniesReducerState } from './types'

const defaultCompaniesReducerState: CompaniesReducerState = {
  parentCompanies: [],
  orphanCompanies: [],
  childrenCompanies: [],

  selectedParent: null,
  selectedChildren: [],
  pendingSelection: [],

  filteringCompaniesIds: [],
  isAllAccountsSelected: false,
  pendingAllAccountsSelection: false,
  isOrphanSelected: false,
}

const companiesActions = {
  SET_PARENT_COMPANIES: 'SET_PARENT_COMPANIES',
  SET_ORPHAN_COMPANIES: 'SET_ORPHAN_COMPANIES',
  SET_CHILDREN_COMPANIES: 'SET_CHILDREN_COMPANIES',
  SET_SELECTED_PARENT: 'SET_SELECTED_PARENT',
  SET_PENDING_SELECTION: 'SET_PENDING_SELECTION',
  APPLY_SELECTION: 'APPLY_SELECTION',
  UPDATE_FILTERING_IDS: 'UPDATE_FILTERING_IDS',
} as const

type CompaniesActions = (typeof companiesActions)[keyof typeof companiesActions]

type CompaniesReducerAction = {
  type: CompaniesActions
  entity?: Company | null
  companies?: Array<Company>
  isAllAccountsSelected?: boolean
}

const companiesReducer = (
  state: CompaniesReducerState,
  action: CompaniesReducerAction,
): CompaniesReducerState => {
  switch (action.type) {
    case companiesActions.SET_PARENT_COMPANIES:
      return { ...state, parentCompanies: action.companies as Array<Company> }

    case companiesActions.SET_ORPHAN_COMPANIES:
      return { ...state, orphanCompanies: action.companies as Array<Company> }

    case companiesActions.SET_CHILDREN_COMPANIES:
      return { ...state, childrenCompanies: action.companies as Array<Company> }

    case companiesActions.SET_SELECTED_PARENT: {
      const newParent = action.entity as Company | null
      const isOrphan = isOrphanParentOption(newParent)

      return {
        ...state,
        selectedParent: newParent,
        selectedChildren: [],
        pendingSelection: [],
        childrenCompanies: [],
        isOrphanSelected: isOrphan,
        isAllAccountsSelected: false,
        pendingAllAccountsSelection: false,
        filteringCompaniesIds: [],
      }
    }

    case companiesActions.SET_PENDING_SELECTION:
      return {
        ...state,
        pendingSelection: (action.companies as Array<Company>) || [],
        pendingAllAccountsSelection: Boolean(action.isAllAccountsSelected),
      }

    case companiesActions.APPLY_SELECTION:
      return {
        ...state,
        selectedChildren: (action.companies as Array<Company>) || [],
        pendingSelection: (action.companies as Array<Company>) || [],
        isAllAccountsSelected: Boolean(action.isAllAccountsSelected),
        pendingAllAccountsSelection: Boolean(action.isAllAccountsSelected),
      }

    case companiesActions.UPDATE_FILTERING_IDS: {
      const { selectedParent, selectedChildren, isOrphanSelected, isAllAccountsSelected } = state
      const toUnique = (ids: Array<string>) => Array.from(new Set(ids))

      if (!selectedParent && !isOrphanSelected) {
        return { ...state, filteringCompaniesIds: [] }
      }

      if (isOrphanSelected) {
        return {
          ...state,
          filteringCompaniesIds: toUnique(
            selectedChildren.map((company) => company.id).filter(Boolean),
          ),
        }
      }

      if (!selectedParent) {
        return { ...state, filteringCompaniesIds: [] }
      }

      if (isAllAccountsSelected) {
        const selectedIds = selectedChildren.map((company) => company.id).filter(Boolean)
        const allAccountsIds = selectedParent.id ? [selectedParent.id, ...selectedIds] : selectedIds

        return {
          ...state,
          filteringCompaniesIds: toUnique(allAccountsIds),
        }
      }

      return {
        ...state,
        filteringCompaniesIds: toUnique(
          selectedChildren.map((company) => company.id).filter(Boolean),
        ),
      }
    }

    default:
      return state
  }
}

export type { CompaniesReducerAction }
export { companiesReducer, defaultCompaniesReducerState, companiesActions }
